import axios from "axios";
import React, {
  useEffect,
  useMemo,
  useRef
} from "react";
import { serverAddress } from "../../config";
import { Global_Data } from "../../globalData/GlobalData";

function EVENT({
  data,
  setTextValue,
  setVarValue,

  setdocumentSelectTableData,
  setdocumentSelectmappingData,
  setDocumentSelectKey,

  setDocumentSelectMainRecordData,
  setDocumentSelectMainRecordObject,
  setIsDocumentSelect,

  setEventSelectTableData,
  setEventSelectTableMapping
}) {
  const {
    token,
    textValue
  } = Global_Data();

  /*
   * Prevent the same automatic document from being loaded
   * repeatedly because mapped textValue fields changed.
   */
  const lastLoadedUrlRef = useRef("");
  const requestSequenceRef = useRef(0);

  const dataSource =
    data?.data?.sDataSource || "";

  const mainRecordMappings = useMemo(() => {
    return Array.isArray(
      data?.data?.mapping?.mainrecord
    )
      ? data.data.mapping.mainrecord
      : [];
  }, [data?.data?.mapping?.mainrecord]);

  const tableRecordMappings = useMemo(() => {
    return Array.isArray(
      data?.data?.mapping?.tablerecord
    )
      ? data.data.mapping.tablerecord
      : [];
  }, [data?.data?.mapping?.tablerecord]);

  const replaceBrowserUrlPlaceholders = (
    pattern
  ) => {
    if (!pattern) {
      return "";
    }

    try {
      const searchParams =
        new URLSearchParams(
          window.location.search
        );

      let resolved =
        String(pattern);

      searchParams.forEach(
        (value, key) => {
          const encodedValue =
            encodeURIComponent(value);

          resolved = resolved
            .replaceAll(
              `[${key}]`,
              encodedValue
            )
            .replaceAll(
              `{${key}}`,
              encodedValue
            );
        }
      );

      return resolved;
    } catch (error) {
      console.error(
        "[EVENT.js] Browser URL substitution failed",
        {
          pattern,
          browserUrl:
            window.location.href,
          error
        }
      );

      return String(pattern);
    }
  };

  const replaceTextValuePlaceholders = (
    pattern,
    values
  ) => {
    return String(pattern || "").replace(
      /\[([^\]]+)\]|\{([^}]+)\}/g,
      (
        match,
        squareKey,
        curlyKey
      ) => {
        const key = String(
          squareKey ||
          curlyKey ||
          ""
        ).trim();

        const value =
          values?.[key];

        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ""
        ) {
          return match;
        }

        return encodeURIComponent(
          String(value).trim()
        );
      }
    );
  };

  const containsUnresolvedPlaceholder =
    value => {
      return (
        /\[[^\]]+\]|\{[^}]+\}/.test(
          String(value || "")
        )
      );
    };

  const dependencyFields = useMemo(() => {
    const fields = [];
    const regex =
      /\[([^\]]+)\]|\{([^}]+)\}/g;

    let match;

    while (
      (match = regex.exec(dataSource))
    ) {
      const fieldName = String(
        match[1] ||
        match[2] ||
        ""
      ).trim();

      if (
        fieldName &&
        !fields.includes(fieldName)
      ) {
        fields.push(fieldName);
      }
    }

    return fields;
  }, [dataSource]);

  const dependencyValues = useMemo(() => {
    return dependencyFields.map(
      fieldName =>
        textValue?.[fieldName] ?? null
    );
  }, [dependencyFields, textValue]);

  const createMappedMainRecord = (
    mainRecord
  ) => {
    const mappedMainRecord = {};

    mainRecordMappings.forEach(
      mapping => {
        const sourceField =
          mapping?.sourceXPath;

        const destinationField =
          mapping?.destinationComponent;

        if (
          !sourceField ||
          !destinationField
        ) {
          return;
        }

        if (
          Object.prototype
            .hasOwnProperty.call(
              mainRecord,
              sourceField
            )
        ) {
          mappedMainRecord[
            destinationField
          ] = mainRecord[sourceField];
        }
      }
    );

    return mappedMainRecord;
  };

  const createTableColumnMapping = (
    tableMapping
  ) => {
    const columnMapping = {};

    const mappings =
      Array.isArray(
        tableMapping?.mapping
      )
        ? tableMapping.mapping
        : [];

    mappings.forEach(mapping => {
      const destinationColumn =
        mapping?.destinationColumn;

      const sourceField =
        String(
          mapping?.sourceXPath || ""
        )
          .split(".")
          .pop();

      if (
        destinationColumn &&
        sourceField
      ) {
        columnMapping[
          destinationColumn
        ] = sourceField;
      }
    });

    return columnMapping;
  };

  const loadEventDocument = async () => {
    const requestId = ++requestSequenceRef.current;

    if (!dataSource) {
      return;
    }

    let resolvedUrl =
      replaceBrowserUrlPlaceholders(
        dataSource
      );

    resolvedUrl =
      replaceTextValuePlaceholders(
        resolvedUrl,
        textValue
      );

    if (
      containsUnresolvedPlaceholder(
        resolvedUrl
      )
    ) {
      console.warn(
        "[EVENT.js] EVENT URL still contains placeholders",
        {
          dataSource,
          resolvedUrl,
          textValue,
          browserUrl:
            window.location.href
        }
      );

      return;
    }

    /*
     * Avoid duplicate loading caused by the mapped
     * sAccountTo/sLocationCode fields updating textValue.
     */
    if (
      lastLoadedUrlRef.current ===
      resolvedUrl
    ) {
      return;
    }

    lastLoadedUrlRef.current =
      resolvedUrl;

    const destinationLoadingTables =
      tableRecordMappings
        .map(tableMapping => {
          const destinationTableName =
            tableMapping?.tablenameDestination;

          if (!destinationTableName) {
            return null;
          }

          return {
            sInputTableName:
              destinationTableName,
            mappingTableName:
              destinationTableName,
            tabledetails: null,
            tablesummary: []
          };
        })
        .filter(Boolean);

    /*
     * EVENT has no visual surface of its own. Route its pending state to the
     * destination table so the component being populated displays the loader.
     */
    if (
      destinationLoadingTables.length > 0 &&
      typeof
        setEventSelectTableData ===
        "function"
    ) {
      setEventSelectTableData({
        data: {
          mainrecord: {},
          tablerecords:
            destinationLoadingTables
        }
      });
    }

    try {
      const response =
        await axios.get(
          `${serverAddress}${resolvedUrl}`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`
            }
          }
        );

      if (requestId !== requestSequenceRef.current) {
        return;
      }

      const responseData =
        response?.data?.data || {};

      const mainRecord =
        responseData?.mainrecord || {};

      const tableRecords =
        Array.isArray(
          responseData?.tablerecords
        )
          ? responseData.tablerecords
          : [];

      /*
       * MAIN RECORD
       */
      const mappedMainRecord =
        createMappedMainRecord(
          mainRecord
        );

      /*
       * Mirror DOCUMENTSELECT state.
       */
      if (
        typeof
          setDocumentSelectMainRecordObject ===
        "function"
      ) {
        setDocumentSelectMainRecordObject(
          mainRecord
        );
      }

      const firstSourceTableName =
        tableRecordMappings?.[0]
          ?.tablenameSource;

      if (
        typeof
          setDocumentSelectMainRecordData ===
        "function"
      ) {
        setDocumentSelectMainRecordData(
          firstSourceTableName
            ? mainRecord?.[
                firstSourceTableName
              ]
            : mainRecord
        );
      }

      if (
        typeof setIsDocumentSelect ===
        "function"
      ) {
        setIsDocumentSelect(true);
      }

      /*
       * Apply EVENT values after the form's initial-default
       * effects have completed. Without this, SettingInitialValues()
       * can restore blank/default values over sAccountTo.
       */
      window.setTimeout(() => {
        if (requestId !== requestSequenceRef.current) {
          return;
        }

        if (
          Object.keys(
            mappedMainRecord
          ).length === 0
        ) {
          return;
        }

        setTextValue(previous => ({
          ...(previous || {}),
          ...mappedMainRecord
        }));

        if (
          typeof setVarValue ===
          "function"
        ) {
          setVarValue(previous => ({
            ...(previous || {}),
            ...mappedMainRecord
          }));
        }
      }, 50);

      /*
       * TABLE RECORDS
       *
       * Follow the exact DOCUMENTSELECT model:
       * keep source rows intact and provide a separate mapping.
       */
      let importedTables = [];
      let combinedColumnMapping = {};

      tableRecordMappings.forEach(
        tableMapping => {
          const sourceTableName =
            tableMapping
              ?.tablenameSource;

          const destinationTableName =
            tableMapping
              ?.tablenameDestination;

          const matchingTables =
            tableRecords.filter(
              record =>
                !sourceTableName ||
                record?.sInputTableName ===
                  sourceTableName
            );

          const mappedTables =
            matchingTables.map(
              record => ({
                ...record,

                mappingTableName:
                  destinationTableName
              })
            );

          importedTables = [
            ...importedTables,
            ...mappedTables
          ];

          combinedColumnMapping = {
            ...combinedColumnMapping,
            ...createTableColumnMapping(
              tableMapping
            )
          };
        }
      );

      /*
       * Populate the same states used by DOCUMENTSELECT.
       */
      if (
        typeof
          setdocumentSelectTableData ===
        "function"
      ) {
        setdocumentSelectTableData(
          importedTables
        );
      }

      if (
        typeof
          setdocumentSelectmappingData ===
        "function"
      ) {
        setdocumentSelectmappingData(
          combinedColumnMapping
        );
      }

      /*
       * Retain EVENT-specific state for backward compatibility.
       */
      if (
        typeof
          setEventSelectTableData ===
        "function"
      ) {
        setEventSelectTableData({
          ...response.data,

          data: {
            ...responseData,

            tablerecords:
              importedTables
          }
        });
      }

      if (
        typeof
          setEventSelectTableMapping ===
        "function"
      ) {
        setEventSelectTableMapping(
          combinedColumnMapping
        );
      }

      /*
       * Remount DEFAULT InputTable only after all mapping/data
       * states have been assigned.
       */
      window.setTimeout(() => {
        if (requestId !== requestSequenceRef.current) {
          return;
        }

        if (
          typeof
            setDocumentSelectKey ===
          "function"
        ) {
          setDocumentSelectKey(
            previous => {
              if (
                previous === "" ||
                previous === undefined ||
                previous === null
              ) {
                return 1;
              }

              return (
                Number(previous) + 1
              );
            }
          );
        }
      }, 75);

      console.log(
        "[EVENT.js] Automatic document loaded",
        {
          resolvedUrl,
          mainRecord,
          mappedMainRecord,
          importedTables,
          combinedColumnMapping
        }
      );
    } catch (error) {
      if (requestId !== requestSequenceRef.current) {
        return;
      }
      /*
       * Allow retry after failure.
       */
      lastLoadedUrlRef.current = "";

      if (
        destinationLoadingTables.length > 0 &&
        typeof
          setEventSelectTableData ===
          "function"
      ) {
        setEventSelectTableData({
          data: {
            mainrecord: {},
            tablerecords:
              destinationLoadingTables.map(
                tableRecord => ({
                  ...tableRecord,
                  tabledetails: []
                })
              )
          }
        });
      }

      console.error(
        "[EVENT.js] Automatic document load failed",
        {
          dataSource,
          resolvedUrl,
          status:
            error?.response?.status,
          response:
            error?.response?.data,
          error
        }
      );
    }
  };

  useEffect(() => {
    loadEventDocument();
  }, [
    dataSource,
    token,
    ...dependencyValues
  ]);

  return null;
}

export default EVENT;
